import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Lock, BrainCircuit, FileText, Mic, ShieldCheck } from "lucide-react";

const TIER_RANK = { "Helden-Basic": 0, "Kurs-Bonus": 1, "Single-Held": 2, "Hauptquartier": 3, "Admin": 99 };

const SINGLE_HELD_FEATURES = [
  { icon: BrainCircuit, label: "Pflege-KI Assistenz", desc: "SIS, Maßnahmenpläne, Pflegeberichte & Kassenbriefe per KI – inkl. Diktat" },
  { icon: FileText, label: "Vollversion aller Premium-PDFs", desc: "Atlas (200+ Seiten), MD-Survival, Pflegesatz-Matrix, Widerspruchs-Guides" },
  { icon: ShieldCheck, label: "Live-Briefings & MDK-Lagezentrum", desc: "Monatliche Zoom-Briefings + PDL-Community mit Live-Antworten" },
  { icon: Mic, label: "Whisper-Diktat", desc: "30 Sek. ins Handy sprechen → fertiger Pflegebericht-Entwurf" },
];

const HAUPTQUARTIER_BONUS = [
  "Stellenanzeigen schalten",
  "Talent-Pool durchsuchen",
  "Pflegesatz-Verhandlungs-Matrix (Excel)",
  "Team-Lizenz für 5 Pflegekräfte",
];


/**
 * Upgrade-CTA-Karte für Dashboard. Sichtbar nur für Helden-Basic & Kurs-Bonus User.
 * Zeigt was bei Single-Held (49 €) freigeschaltet wird – plus dezenter Hinweis auf Hauptquartier.
 */
export default function UpgradePromoCard({ user }) {
  const tier = user?.tier || "Helden-Basic";
  const rank = TIER_RANK[tier] ?? 0;
  // Nur für Basic + Kurs-Bonus anzeigen (Single-Held & höher haben schon alles Wichtige)
  if (rank >= 2) return null;

  return (
    <section data-testid="upgrade-promo-card"
      className="rounded-3xl border border-terra/30 bg-gradient-to-br from-peach-50 via-cream-50 to-white p-7 lg:p-9 relative overflow-hidden">
      {/* Subtler Decoration */}
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-terra/8 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-16 -left-12 w-56 h-56 rounded-full bg-brand-darkred/8 blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="inline-flex items-center gap-2 rounded-full bg-terra/12 text-terra px-3 h-7 text-[10.5px] uppercase tracking-[0.22em] font-bold mb-4">
          <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
          Du bist {tier}
        </div>
        <h2 className="font-display text-sand-800 text-[1.7rem] lg:text-[2rem] font-semibold leading-[1.15]">
          Was du als <span className="text-terra">Single-Held (49&nbsp;€)</span> freischaltest.
        </h2>
        <p className="mt-3 text-sand-700 text-[14.5px] leading-[1.6] max-w-2xl">
          Du hast aktuell Zugriff auf Basis-Schatzkiste, MDK-Lagezentrum & Briefings im Read-Only-Modus.
          Im <strong>Single-Held-Tier</strong> kommen die schweren Werkzeuge dazu, mit denen du als Pflegekraft
          oder PDL <em>sofort spürbar Zeit sparst</em>.
        </p>

        <ul className="mt-7 grid sm:grid-cols-2 gap-3"
          data-testid="upgrade-features">
          {SINGLE_HELD_FEATURES.map((f, i) => (
            <li key={i} className="flex items-start gap-3 rounded-2xl bg-white/70 border border-cream-300 p-4">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-terra/12 text-terra">
                <f.icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
              </span>
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-sand-800">{f.label}</div>
                <div className="text-[12.5px] text-sand-600 leading-snug">{f.desc}</div>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
          <Link to="/campus/pro" data-testid="upgrade-promo-cta-pro"
            className="inline-flex items-center justify-between gap-2 rounded-full bg-terra hover:bg-terra-dark text-white h-12 px-6 text-[14px] font-semibold transition-colors">
            Auf Single-Held upgraden · 49 €/Monat
            <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
          <Link to="/campus/team" data-testid="upgrade-promo-cta-team"
            className="inline-flex items-center gap-2 text-[13.5px] font-medium text-brand-darkred underline-offset-4 hover:underline">
            Mehrere Pflegekräfte? Hauptquartier ansehen
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
        </div>

        <div className="mt-6 pt-5 border-t border-cream-300 text-[12px] text-sand-500 leading-relaxed">
          <strong className="text-sand-700">Im Hauptquartier-Tier zusätzlich:</strong>{" "}
          {HAUPTQUARTIER_BONUS.join(" · ")}
          <span className="ml-2 inline-flex items-center gap-1 text-sand-500">
            <Lock className="h-3 w-3" strokeWidth={2} /> alle Inhalte 30 Tage kündbar.
          </span>
        </div>
      </div>
    </section>
  );
}
